import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { api, getErrorMessage } from "@/lib/seoApi";
import { useAuth } from "@/lib/auth";
import { useLang } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import {
  MapPin, AlertTriangle, LogIn, Star, Phone, Globe2, CheckCircle2, XCircle, Trophy,
} from "lucide-react";

const T = {
  en: {
    title: "Local Rank Checker",
    sub: "See exactly where your business ranks on Google Maps for the searches your customers type. Top 40 results checked per keyword, competitors included.",
    gateTitle: "Sign in to check your local ranking",
    gateBody: "Free accounts include 5 local rank checks per day - each check queries live Google Maps results.",
    gateCta: "Sign in or create a free account",
    businessName: "Business name",
    businessPh: "Ex: Restaurant Le Gourmet",
    city: "City",
    cityPh: "Ex: Montréal",
    keywords: "Search keywords (1 to 5)",
    kwRequired: "Keyword 1 (required)",
    kwOptional: (n) => `Keyword ${n} (optional)`,
    run: "Check my ranking",
    running: "Checking Google Maps...",
    quota: "Live Google Maps data - 5 checks per day with your free account.",
    left: (n) => ` ${n} left today.`,
    errTitle: "Check failed",
    errEmpty: "Business name, city and at least one keyword are required",
    loadingTitle: "Querying Google Maps for each keyword...",
    loadingSub: "Usually takes 5-20 seconds.",
    bestRank: "Best rank:",
    notTop40: "Not in top 40",
    top3Msg: "Your business appears in the Google Maps top 3 - the coveted 'local pack'. Keep it up!",
    foundMsg: "Your business was found. Aim for the top 3 ('local pack') - that's where most clicks go.",
    notFoundMsg: "Your business was not found in the top 40 for these keywords. Claim and optimize your Google Business Profile to enter the rankings.",
    profileTitle: "Your Google Business Profile",
    noAddress: "No address listed",
    reviews: (r, n) => `${r} / 5 (${n} reviews)`,
    noReviews: "No reviews yet",
    noPhone: "No phone listed",
    noWebsite: "No website listed",
    claimed: "Profile appears claimed",
    unclaimed: "Profile appears unclaimed - claim it now!",
    unknown: "Profile status unknown",
    rankByKw: (c) => `Ranking by keyword in ${c}`,
    thKeyword: "Keyword",
    thPosition: "Position (top 40)",
    competitors: "Who ranks above you (top 20 per keyword)",
    results: (n) => `${n} results`,
    thBusiness: "Business",
    thRating: "Rating",
    thReviews: "Reviews",
    you: "You",
    emptyBody: "Enter your business name, city and the keywords your customers search for. We check the live Google Maps top 40 for each keyword and show where you rank - plus the competitors above you.",
  },
  fr: {
    title: "Vérificateur de rang local",
    sub: "Voyez exactement où votre entreprise se classe sur Google Maps pour les recherches de vos clients. Top 40 vérifié par mot-clé, concurrents inclus.",
    gateTitle: "Connectez-vous pour vérifier votre rang local",
    gateBody: "Le compte gratuit inclut 5 vérifications par jour - chaque vérification interroge Google Maps en direct.",
    gateCta: "Connexion ou création de compte gratuit",
    businessName: "Nom de l'entreprise",
    businessPh: "Ex : Restaurant Le Gourmet",
    city: "Ville",
    cityPh: "Ex : Montréal",
    keywords: "Mots-clés de recherche (1 à 5)",
    kwRequired: "Mot-clé 1 (requis)",
    kwOptional: (n) => `Mot-clé ${n} (optionnel)`,
    run: "Vérifier mon rang",
    running: "Interrogation de Google Maps...",
    quota: "Données Google Maps en direct - 5 vérifications par jour avec le compte gratuit.",
    left: (n) => ` Il en reste ${n} aujourd'hui.`,
    errTitle: "Échec de la vérification",
    errEmpty: "Nom d'entreprise, ville et au moins un mot-clé sont requis",
    loadingTitle: "Interrogation de Google Maps pour chaque mot-clé...",
    loadingSub: "Habituellement 5 à 20 secondes.",
    bestRank: "Meilleur rang :",
    notTop40: "Absent du top 40",
    top3Msg: "Votre entreprise est dans le top 3 de Google Maps - le fameux « local pack ». Continuez!",
    foundMsg: "Votre entreprise a été trouvée. Visez le top 3 (« local pack ») - c'est là que vont la plupart des clics.",
    notFoundMsg: "Votre entreprise n'a pas été trouvée dans le top 40 pour ces mots-clés. Réclamez et optimisez votre profil Google Business pour entrer dans le classement.",
    profileTitle: "Votre profil Google Business",
    noAddress: "Aucune adresse affichée",
    reviews: (r, n) => `${r} / 5 (${n} avis)`,
    noReviews: "Aucun avis pour l'instant",
    noPhone: "Aucun téléphone affiché",
    noWebsite: "Aucun site web affiché",
    claimed: "Profil apparemment réclamé",
    unclaimed: "Profil apparemment non réclamé - réclamez-le!",
    unknown: "Statut du profil inconnu",
    rankByKw: (c) => `Classement par mot-clé à ${c}`,
    thKeyword: "Mot-clé",
    thPosition: "Position (top 40)",
    competitors: "Qui se classe devant vous (top 20 par mot-clé)",
    results: (n) => `${n} résultats`,
    thBusiness: "Entreprise",
    thRating: "Note",
    thReviews: "Avis",
    you: "Vous",
    emptyBody: "Entrez le nom de votre entreprise, votre ville et les mots-clés que vos clients recherchent. Nous vérifions le top 40 de Google Maps en direct pour chaque mot-clé et montrons votre rang - plus les concurrents devant vous.",
  },
};

export default function LocalRank() {
  const [businessName, setBusinessName] = useState("");
  const [city, setCity] = useState("");
  const [keywords, setKeywords] = useState(["", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const { user, ready } = useAuth();
  const { lang } = useLang();
  const t = T[lang];
  const location = useLocation();

  const rankBadge = (rank) => {
    if (!rank) return { label: t.notTop40, cls: "bg-red-50 text-red-700 border-red-200" };
    if (rank <= 3) return { label: `#${rank}`, cls: "bg-emerald-50 text-emerald-700 border-emerald-200" };
    if (rank <= 10) return { label: `#${rank}`, cls: "bg-amber-50 text-amber-700 border-amber-200" };
    return { label: `#${rank}`, cls: "bg-red-50 text-red-700 border-red-200" };
  };

  const setKeyword = (i, v) => setKeywords((prev) => prev.map((k, idx) => (idx === i ? v : k)));

  const search = async (e) => {
    e?.preventDefault();
    const kws = keywords.map((k) => k.trim()).filter(Boolean);
    if (!businessName.trim() || !city.trim() || kws.length === 0) {
      setError(t.errEmpty);
      return;
    }
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const { data } = await api.post(
        "/gmb/rank",
        { business_name: businessName.trim(), city: city.trim(), keywords: kws },
        { timeout: 120000 }
      );
      setResult(data);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const info = result?.business_info;
  const best = result ? rankBadge(result.best_rank) : null;

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-10 py-8 sm:py-10">
      <div className="max-w-2xl">
        <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight">{t.title}</h1>
        <p className="mt-2 text-muted-foreground">{t.sub}</p>
      </div>

      {ready && !user ? (
        <div className="mt-8 rounded-lg border p-8 text-center max-w-2xl bg-card" data-testid="local-rank-signin-gate">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-accent text-accent-foreground">
            <LogIn className="h-6 w-6" />
          </div>
          <h2 className="mt-4 text-lg font-heading font-semibold">{t.gateTitle}</h2>
          <p className="mt-2 text-sm text-muted-foreground">{t.gateBody}</p>
          <Button asChild className="mt-5" size="lg" data-testid="local-rank-signin-button">
            <Link to={`/login?next=${encodeURIComponent(location.pathname)}`}>{t.gateCta}</Link>
          </Button>
        </div>
      ) : (
        <Card className="mt-6 max-w-2xl">
          <CardContent className="p-5 sm:p-6">
            <form onSubmit={search} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="lr-business">{t.businessName}</Label>
                  <Input
                    id="lr-business"
                    value={businessName}
                    onChange={(e) => setBusinessName(e.target.value)}
                    placeholder={t.businessPh}
                    data-testid="local-rank-business-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="lr-city">{t.city}</Label>
                  <Input
                    id="lr-city"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    placeholder={t.cityPh}
                    data-testid="local-rank-city-input"
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>{t.keywords}</Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {keywords.map((k, i) => (
                    <Input
                      key={i}
                      value={k}
                      onChange={(e) => setKeyword(i, e.target.value)}
                      placeholder={i === 0 ? t.kwRequired : t.kwOptional(i + 1)}
                      data-testid={`local-rank-keyword-input-${i}`}
                    />
                  ))}
                </div>
              </div>
              <Button type="submit" size="lg" disabled={loading || !ready} className="h-11" data-testid="local-rank-run-button">
                <MapPin className="h-4 w-4 mr-2" />
                {loading ? t.running : t.run}
              </Button>
              <p className="text-xs text-muted-foreground">
                {t.quota}
                {result?.searches_left_today != null && t.left(result.searches_left_today)}
              </p>
            </form>
          </CardContent>
        </Card>
      )}

      {error && (
        <Alert variant="destructive" className="mt-6 max-w-2xl" data-testid="local-rank-error">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>{t.errTitle}</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {loading && (
        <div className="mt-8 space-y-4" data-testid="local-rank-loading">
          <div className="rounded-lg border bg-card p-5 flex items-center gap-4">
            <MapPin className="h-6 w-6 text-primary animate-pulse shrink-0" />
            <div>
              <p className="text-sm font-medium">{t.loadingTitle}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{t.loadingSub}</p>
            </div>
          </div>
          <Skeleton className="h-32 rounded-lg" />
          <Skeleton className="h-48 rounded-lg" />
        </div>
      )}

      {result && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} className="mt-8 space-y-6">
          {/* Summary */}
          <Card>
            <CardContent className="p-5 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center gap-5">
                <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg bg-accent text-accent-foreground">
                  <Trophy className="h-7 w-7" />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium">{result.business_name}</span>
                    <Badge variant="secondary" className="text-xs">{result.city}</Badge>
                    <Badge variant="outline" className={`${best.cls} text-xs`} data-testid="local-rank-best-badge">
                      {t.bestRank} {best.label}
                    </Badge>
                  </div>
                  <p className="mt-2 text-xs text-muted-foreground">
                    {result.best_rank
                      ? result.best_rank <= 3
                        ? t.top3Msg
                        : t.foundMsg
                      : t.notFoundMsg}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Business info */}
          {info && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-heading">{t.profileTitle}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm" data-testid="local-rank-business-info">
                  <div className="rounded-md border p-3">
                    <p className="font-medium">{info.name}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{info.address || t.noAddress}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{info.type || ""}</p>
                  </div>
                  <div className="rounded-md border p-3 space-y-1.5">
                    <p className="flex items-center gap-2 text-xs">
                      <Star className="h-3.5 w-3.5 text-amber-500" />
                      {info.rating ? t.reviews(info.rating, info.reviews || 0) : t.noReviews}
                    </p>
                    <p className="flex items-center gap-2 text-xs">
                      <Phone className="h-3.5 w-3.5 text-muted-foreground" />
                      {info.phone || t.noPhone}
                    </p>
                    <p className="flex items-center gap-2 text-xs">
                      <Globe2 className="h-3.5 w-3.5 text-muted-foreground" />
                      {info.website ? (
                        <a href={info.website} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline break-all">
                          {info.website.replace(/^https?:\/\//, "").slice(0, 40)}
                        </a>
                      ) : t.noWebsite}
                    </p>
                    <p className="flex items-center gap-2 text-xs">
                      {info.gmb_claimed ? (
                        <><CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> {t.claimed}</>
                      ) : info.gmb_claimed === false ? (
                        <><XCircle className="h-3.5 w-3.5 text-red-600" /> {t.unclaimed}</>
                      ) : (
                        <>{t.unknown}</>
                      )}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Rank per keyword */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-heading">{t.rankByKw(result.city)}</CardTitle>
            </CardHeader>
            <CardContent className="px-0">
              <Table data-testid="local-rank-keywords-table">
                <TableHeader>
                  <TableRow>
                    <TableHead className="pl-6">{t.thKeyword}</TableHead>
                    <TableHead className="pr-6">{t.thPosition}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {result.keyword_results.map((kr) => {
                    const b = rankBadge(kr.business_rank);
                    return (
                      <TableRow key={kr.keyword}>
                        <TableCell className="pl-6 text-sm font-medium">{kr.keyword}</TableCell>
                        <TableCell className="pr-6">
                          <Badge variant="outline" className={`${b.cls} text-xs`}>{b.label}</Badge>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Competitors */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-heading">{t.competitors}</CardTitle>
            </CardHeader>
            <CardContent>
              <Accordion type="multiple" className="w-full" data-testid="local-rank-competitors">
                {result.keyword_results.map((kr) => (
                  <AccordionItem key={kr.keyword} value={kr.keyword}>
                    <AccordionTrigger className="text-sm hover:no-underline">
                      <span className="flex items-center gap-2">
                        <span className="font-medium">{kr.keyword}</span>
                        <Badge variant="secondary" className="text-[10px]">{t.results(kr.top_businesses.length)}</Badge>
                      </span>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="max-h-[400px] overflow-auto rounded-md border">
                        <Table>
                          <TableHeader className="sticky top-0 bg-card z-10">
                            <TableRow>
                              <TableHead className="pl-4 w-10">#</TableHead>
                              <TableHead>{t.thBusiness}</TableHead>
                              <TableHead>{t.thRating}</TableHead>
                              <TableHead className="pr-4">{t.thReviews}</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {kr.top_businesses.map((b) => (
                              <TableRow key={b.position} className={kr.business_rank === b.position ? "bg-emerald-50/60" : ""}>
                                <TableCell className="pl-4 tabular-nums text-xs">{b.position}</TableCell>
                                <TableCell className="text-xs">
                                  <span className="font-medium">{b.title}</span>
                                  {kr.business_rank === b.position && <Badge className="ml-2 text-[10px]">{t.you}</Badge>}
                                  <span className="block text-[11px] text-muted-foreground line-clamp-1">{b.address}</span>
                                </TableCell>
                                <TableCell className="text-xs tabular-nums">{b.rating ?? "-"}</TableCell>
                                <TableCell className="pr-4 text-xs tabular-nums">{b.reviews ?? "-"}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {!result && !loading && !error && user && (
        <div className="mt-12 rounded-lg border border-dashed p-10 text-center max-w-2xl" data-testid="local-rank-empty-state">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-accent text-accent-foreground">
            <MapPin className="h-6 w-6" />
          </div>
          <p className="mt-3 text-sm text-muted-foreground">{t.emptyBody}</p>
        </div>
      )}
    </div>
  );
}
